import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { FilterMap } from 'models';

/**
 * Dashboard UI / derived state (NOT owned by the RTK Query server cache).
 * Mirrors §6 "Slices → dashboardSlice" of PHASE2_DASHBOARD_INVENTORY.md.
 */
export interface DashboardState {
  // Sort — derived from user.dashboard_projects_default_order ('-'=desc, 'manual'=custom)
  sortBy: string | null;
  sortOrder: 'asc' | 'desc';
  isManual: boolean;

  // Column searches + name filter (debounced 500ms before commit, by the UI)
  filters: FilterMap;

  // Status rail selection ('' = All)
  taskFilter: string;

  // Active/Archived/All dashboard toggle (dev delta 2026-06, container.js).
  // Sent as the `project_state` query param on BOTH lists ('state' was taken by
  // the region field on ProjectFilter). Persisted in localStorage
  // 'dashboard_project_state' so the Integrated Project Ledger CSV export can
  // mirror it. NOT a user "filter" — must not trip Filtered-count / .no-drag.
  projectState: 'active' | 'archived' | 'all';

  // Single-select / keyboard nav
  selectedRowId: number | null;

  // Project ids with an unread `new_project` notification (→ .unread-notification
  // until clicked, parity 78). Populated by realtime (2d); cleared on row click.
  unreadProjectIds: number[];

  // Left rail collapse
  collapsed: boolean;

  // Infinite scroll
  page: number;
  hasMore: boolean;

  // Visible column ids in order (mirrors user.manage_columns)
  manageColumns: number[];

  // Modal / popup open flags
  modals: {
    newProject: boolean;
    columnSettings: boolean;
    deleteConfirm: number | null; // projectId being confirmed for delete
    sortConfirm: string | null; // field name pending sort confirm
    noPermission: boolean;
  };
}

/** Initial Active/Archived/All state from localStorage (container.js semantics). */
function initialProjectState(): DashboardState['projectState'] {
  try {
    const stored = window.localStorage?.getItem('dashboard_project_state');
    return stored === 'all' || stored === 'archived' ? stored : 'active';
  } catch {
    return 'active';
  }
}

const initialState: DashboardState = {
  sortBy: null,
  sortOrder: 'asc',
  isManual: false,
  filters: {},
  taskFilter: '',
  projectState: initialProjectState(),
  selectedRowId: null,
  unreadProjectIds: [],
  collapsed: false,
  page: 1,
  hasMore: true,
  manageColumns: [],
  modals: {
    newProject: false,
    columnSettings: false,
    deleteConfirm: null,
    sortConfirm: null,
    noPermission: false,
  },
};

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    // ---- Sort ----
    setSort: (
      state,
      action: PayloadAction<{
        sortBy: string | null;
        sortOrder?: 'asc' | 'desc';
        isManual?: boolean;
      }>
    ) => {
      state.sortBy = action.payload.sortBy;
      if (action.payload.sortOrder) state.sortOrder = action.payload.sortOrder;
      state.isManual = action.payload.isManual ?? false;
    },
    /** Initialize sort state from `dashboard_projects_default_order`. */
    setSortFromUserOrder: (state, action: PayloadAction<string | undefined | null>) => {
      const order = action.payload;
      if (!order || order === 'manual') {
        state.isManual = order === 'manual';
        state.sortBy = order === 'manual' ? null : state.sortBy;
        return;
      }
      state.isManual = false;
      if (order.startsWith('-')) {
        state.sortBy = order.slice(1);
        state.sortOrder = 'desc';
      } else {
        state.sortBy = order;
        state.sortOrder = 'asc';
      }
    },

    // ---- Filters ----
    setFilter: (state, action: PayloadAction<{ field: string; value: string }>) => {
      const { field, value } = action.payload;
      if (value === '') delete state.filters[field];
      else state.filters[field] = value;
      // Filtering resets pagination.
      state.page = 1;
      state.hasMore = true;
    },
    setFilters: (state, action: PayloadAction<FilterMap>) => {
      state.filters = action.payload;
      state.page = 1;
      state.hasMore = true;
    },
    clearFilters: (state) => {
      state.filters = {};
      state.page = 1;
      state.hasMore = true;
    },

    // ---- Status rail ----
    setTaskFilter: (state, action: PayloadAction<string>) => {
      state.taskFilter = action.payload;
      state.page = 1;
      state.hasMore = true;
    },

    // ---- Active/Archived/All toggle (dev delta) ----
    setProjectState: (state, action: PayloadAction<DashboardState['projectState']>) => {
      state.projectState = action.payload;
      state.page = 1;
      state.hasMore = true;
      try {
        window.localStorage?.setItem('dashboard_project_state', action.payload);
      } catch {
        /* ignore (private mode) */
      }
    },

    // ---- Selection ----
    setSelectedRow: (state, action: PayloadAction<number | null>) => {
      state.selectedRowId = action.payload;
    },

    // ---- Unread new_project highlight (parity 78) ----
    setUnreadProjects: (state, action: PayloadAction<number[]>) => {
      state.unreadProjectIds = action.payload;
    },
    clearUnreadProject: (state, action: PayloadAction<number>) => {
      state.unreadProjectIds = state.unreadProjectIds.filter((id) => id !== action.payload);
    },

    // ---- Collapse ----
    setCollapsed: (state, action: PayloadAction<boolean>) => {
      state.collapsed = action.payload;
    },
    toggleCollapsed: (state) => {
      state.collapsed = !state.collapsed;
    },

    // ---- Pagination ----
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    nextPage: (state) => {
      state.page += 1;
    },
    setHasMore: (state, action: PayloadAction<boolean>) => {
      state.hasMore = action.payload;
    },

    // ---- Columns ----
    setManageColumns: (state, action: PayloadAction<number[]>) => {
      state.manageColumns = action.payload;
    },

    // ---- Modals ----
    openNewProject: (state) => {
      state.modals.newProject = true;
    },
    closeNewProject: (state) => {
      state.modals.newProject = false;
    },
    openColumnSettings: (state) => {
      state.modals.columnSettings = true;
    },
    closeColumnSettings: (state) => {
      state.modals.columnSettings = false;
    },
    openDeleteConfirm: (state, action: PayloadAction<number>) => {
      state.modals.deleteConfirm = action.payload;
    },
    closeDeleteConfirm: (state) => {
      state.modals.deleteConfirm = null;
    },
    openSortConfirm: (state, action: PayloadAction<string>) => {
      state.modals.sortConfirm = action.payload;
    },
    closeSortConfirm: (state) => {
      state.modals.sortConfirm = null;
    },
    openNoPermission: (state) => {
      state.modals.noPermission = true;
    },
    closeNoPermission: (state) => {
      state.modals.noPermission = false;
    },
  },
});

export const {
  setSort,
  setSortFromUserOrder,
  setFilter,
  setFilters,
  clearFilters,
  setTaskFilter,
  setProjectState,
  setSelectedRow,
  setUnreadProjects,
  clearUnreadProject,
  setCollapsed,
  toggleCollapsed,
  setPage,
  nextPage,
  setHasMore,
  setManageColumns,
  openNewProject,
  closeNewProject,
  openColumnSettings,
  closeColumnSettings,
  openDeleteConfirm,
  closeDeleteConfirm,
  openSortConfirm,
  closeSortConfirm,
  openNoPermission,
  closeNoPermission,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;
